import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Switch,
  FormControlLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyIcon from '@mui/icons-material/Key';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import PsychologyIcon from '@mui/icons-material/Psychology';
import TuneIcon from '@mui/icons-material/Tune';
import BuildIcon from '@mui/icons-material/Build';
import ScienceIcon from '@mui/icons-material/Science';
import SettingsIcon from '@mui/icons-material/Settings';
import api from '../services/api';
import { AIAgent } from '../types/ai';
import { useToast } from '../hooks/useToast';

// Glassmorphism styles
const glassStyle = {
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
};

const glassCardStyle = {
  background: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  borderRadius: 3,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(99, 102, 241, 0.15)',
    borderColor: 'rgba(99, 102, 241, 0.3)',
  }
};

// Modelos fallback (usado quando a API não está disponível)
const FALLBACK_MODELS: Record<string, { value: string; label: string }[]> = {
  openai: [
    { value: 'gpt-5.2', label: 'GPT-5.2 (Recomendado)' },
    { value: 'gpt-5.2-mini', label: 'GPT-5.2 Mini' },
    { value: 'gpt-4.1', label: 'GPT-4.1' },
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
  ],
  anthropic: [
    { value: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5 (Mais capaz)' },
    { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4 (Recomendado)' },
    { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
    { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku' },
  ],
  gemini: [
    { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash (Recomendado)' },
    { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
    { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
  ],
};

const DEFAULT_PROMPT = `Você é um assistente de IA profissional e prestativo.

Suas responsabilidades:
- Responder de forma clara e objetiva
- Ser educado e profissional
- Ajudar o usuário a resolver suas dúvidas

Diretrizes:
- Mantenha as respostas concisas
- Use linguagem adequada ao contexto
- Se não souber algo, seja honesto`;

interface AgentFormData {
  name: string;
  type: string;
  description: string;
  is_active: boolean;
  system_prompt: string;
  provider: string;
  model: string;
  temperature: number;
  max_tokens: number;
  summary_after_messages: number;
  enabled_tools: string[];
}

const defaultFormData: AgentFormData = {
  name: '',
  type: 'vendas',
  description: '',
  is_active: true,
  system_prompt: DEFAULT_PROMPT,
  provider: 'openai',
  model: 'gpt-5.2',
  temperature: 0.7,
  max_tokens: 2000,
  summary_after_messages: 20,
  enabled_tools: [],
};

// Helper para extrair dados paginados
const extractData = <T,>(response: any): T[] => {
  if (Array.isArray(response.data)) return response.data;
  if (response.data?.results) return response.data.results;
  return [];
};

export const AIAgents: React.FC = () => {
  const [agents, setAgents] = useState<AIAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<string>('all');

  // Available models (static list)
  const availableModels = FALLBACK_MODELS;

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<AIAgent | null>(null);
  const [formData, setFormData] = useState<AgentFormData>(defaultFormData);
  const [configTab, setConfigTab] = useState(0);

  // API Key modal
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [selectedAgentForKey, setSelectedAgentForKey] = useState<AIAgent | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [savingApiKey, setSavingApiKey] = useState(false);

  // Test modal
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testingAgent, setTestingAgent] = useState<AIAgent | null>(null);
  const [testMessage, setTestMessage] = useState('');
  const [testResponse, setTestResponse] = useState('');
  const [testing, setTesting] = useState(false);

  const { showToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ai/agents/?show_inactive=true');
      const agentsData = extractData<AIAgent>(response);
      setAgents(agentsData);
    } catch (error: any) {
      showToast('Erro ao carregar agentes', 'error');
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingAgent(null);
    setFormData(defaultFormData);
    setConfigTab(0);
    setEditModalOpen(true);
  };

  const handleEdit = (agent: AIAgent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.name,
      type: agent.type,
      description: agent.description || '',
      is_active: agent.is_active,
      system_prompt: agent.system_prompt,
      provider: agent.provider,
      model: agent.model,
      temperature: agent.temperature,
      max_tokens: agent.max_tokens,
      summary_after_messages: agent.summary_after_messages || 20,
      enabled_tools: agent.enabled_tools || [],
    });
    setConfigTab(0);
    setEditModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      showToast('Nome do agente é obrigatório', 'error');
      return;
    }
    if (!formData.system_prompt.trim()) {
      showToast('Prompt do sistema é obrigatório', 'error');
      return;
    }

    setSaving(true);
    try {
      if (editingAgent) {
        await api.patch(`/ai/agents/${editingAgent.id}/`, formData);
        showToast('Agente atualizado com sucesso!', 'success');
      } else {
        await api.post('/ai/agents/', formData);
        showToast('Agente criado com sucesso!', 'success');
      }
      setEditModalOpen(false);
      setEditingAgent(null);
      loadAgents();
    } catch (error: any) {
      console.error('Erro ao salvar agente:', error);
      showToast(error.response?.data?.detail || 'Erro ao salvar agente', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (agent: AIAgent) => {
    if (!window.confirm(`Tem certeza que deseja excluir o agente "${agent.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/ai/agents/${agent.id}/`);
      showToast('Agente excluído com sucesso!', 'success');
      loadAgents();
    } catch (error) {
      console.error('Erro ao excluir agente:', error);
      showToast('Erro ao excluir agente', 'error');
    }
  };

  const handleToggleAgent = async (agent: AIAgent) => {
    try {
      await api.patch(`/ai/agents/${agent.id}/`, { is_active: !agent.is_active });
      showToast(`Agente ${agent.is_active ? 'desativado' : 'ativado'} com sucesso!`, 'success');
      loadAgents();
    } catch (error) {
      showToast('Erro ao alterar status do agente', 'error');
    }
  };

  const handleOpenApiKeyModal = (agent: AIAgent) => {
    setSelectedAgentForKey(agent);
    setApiKey('');
    setApiKeyModalOpen(true);
  };

  const handleSaveApiKey = async () => {
    if (!selectedAgentForKey || !apiKey.trim()) return;

    try {
      setSavingApiKey(true);
      await api.post(`/ai/agents/${selectedAgentForKey.id}/configure_api_key/`, {
        api_key: apiKey
      });
      showToast('API Key configurada com sucesso!', 'success');
      setApiKeyModalOpen(false);
      setApiKey('');
      loadAgents();
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Erro ao configurar API key', 'error');
    } finally {
      setSavingApiKey(false);
    }
  };

  const handleOpenTest = (agent: AIAgent) => {
    setTestingAgent(agent);
    setTestMessage('');
    setTestResponse('');
    setTestModalOpen(true);
  };

  const handleTest = async () => {
    if (!testingAgent || !testMessage.trim()) {
      showToast('Digite uma mensagem para testar', 'error');
      return;
    }

    setTesting(true);
    setTestResponse('');
    try {
      const response = await api.post(`/ai/agents/${testingAgent.id}/test/`, {
        message: testMessage,
      });
      setTestResponse(response.data.response || JSON.stringify(response.data, null, 2));
      showToast('Teste executado!', 'success');
    } catch (error: any) {
      console.error('Erro ao testar agente:', error);
      setTestResponse(`Erro: ${error.response?.data?.error || error.message}`);
      showToast('Erro ao testar agente', 'error');
    } finally {
      setTesting(false);
    }
  };

  const handleProviderChange = (provider: string) => {
    const models = availableModels[provider] || FALLBACK_MODELS[provider] || [];
    setFormData({
      ...formData,
      provider,
      model: models[0]?.value || '',
    });
  };

  const handleToolToggle = (tool: string) => {
    const currentTools = formData.enabled_tools || [];
    if (currentTools.includes(tool)) {
      setFormData({
        ...formData,
        enabled_tools: currentTools.filter(t => t !== tool),
      });
    } else {
      setFormData({
        ...formData,
        enabled_tools: [...currentTools, tool],
      });
    }
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      'atendimento': { bg: 'rgba(59, 130, 246, 0.1)', text: '#3b82f6', border: 'rgba(59, 130, 246, 0.3)' },
      'suporte': { bg: 'rgba(16, 185, 129, 0.1)', text: '#10b981', border: 'rgba(16, 185, 129, 0.3)' },
      'vendas': { bg: 'rgba(139, 92, 246, 0.1)', text: '#8b5cf6', border: 'rgba(139, 92, 246, 0.3)' }
    };
    return colors[type] || { bg: 'rgba(107, 114, 128, 0.1)', text: '#6b7280', border: 'rgba(107, 114, 128, 0.3)' };
  };

  const getProviderIcon = (provider: string) => {
    const icons: Record<string, { icon: string; color: string }> = {
      'openai': { icon: '🤖', color: '#10b981' },
      'anthropic': { icon: '🧠', color: '#8b5cf6' },
      'gemini': { icon: '💎', color: '#3b82f6' }
    };
    return icons[provider] || { icon: '🤖', color: '#6b7280' };
  };

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(search.toLowerCase()) ||
                         agent.description?.toLowerCase().includes(search.toLowerCase());
    const matchesType = filterType === 'all' || agent.type === filterType;
    return matchesSearch && matchesType;
  });

  const stats = {
    total: agents.length,
    active: agents.filter(a => a.is_active).length,
    configured: agents.filter(a => a.enabled_tools && a.enabled_tools.length > 0).length,
    needsSetup: agents.filter(a => !a.enabled_tools || a.enabled_tools.length === 0).length
  };

  const availableTools = [
    { id: 'consultar_lead', name: 'Consultar Lead', description: 'Busca informações de leads' },
    { id: 'criar_lead', name: 'Criar Lead', description: 'Cria novos leads no sistema' },
    { id: 'criar_negocio', name: 'Criar Negócio', description: 'Cria negócios no pipeline' },
    { id: 'mover_negocio', name: 'Mover Negócio', description: 'Move negócios entre etapas' },
    { id: 'atualizar_campo_lead', name: 'Atualizar Lead', description: 'Atualiza campos do lead' },
    { id: 'consultar_pricing', name: 'Consultar Preços', description: 'Consulta tabela de preços' },
  ];

  if (loading) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)',
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#6366f1', mb: 2 }} />
          <Typography sx={{ color: '#6b7280' }}>Carregando agentes...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      height: '100%',
      width: '100%',
      overflow: 'auto',
      background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)',
      p: 3,
    }}>
      {/* Header */}
      <Box sx={{
        ...glassStyle,
        borderRadius: 3,
        px: 3,
        py: 2.5,
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 48,
            height: 48,
            borderRadius: 2.5,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SmartToyIcon sx={{ color: '#fff', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>
              Agentes de IA
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Configure e gerencie seus assistentes inteligentes
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title="Atualizar">
            <IconButton onClick={loadAgents} sx={{
              background: 'rgba(255,255,255,0.8)',
              '&:hover': { background: 'rgba(255,255,255,0.95)' }
            }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => navigate('/ai-settings')}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: 'rgba(99, 102, 241, 0.5)',
              color: '#6366f1',
              background: 'rgba(255,255,255,0.8)',
              '&:hover': {
                borderColor: '#6366f1',
                background: 'rgba(99, 102, 241, 0.1)',
              }
            }}
          >
            Configurações
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
              }
            }}
          >
            Novo Agente
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
        {[
          { label: 'Total de Agentes', value: stats.total, color: '#6366f1', icon: <SmartToyIcon /> },
          { label: 'Agentes Ativos', value: stats.active, color: '#10b981', icon: <CheckCircleIcon /> },
          { label: 'Configurados', value: stats.configured, color: '#3b82f6', icon: <TuneIcon /> },
          { label: 'Precisam Setup', value: stats.needsSetup, color: '#f59e0b', icon: <WarningIcon /> },
        ].map((stat, index) => (
          <Box key={index} sx={{
            ...glassStyle,
            borderRadius: 2.5,
            p: 2.5,
            boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                {stat.label}
              </Typography>
              <Box sx={{ color: stat.color, opacity: 0.7 }}>
                {stat.icon}
              </Box>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: stat.color }}>
              {stat.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Filters */}
      <Box sx={{
        ...glassStyle,
        borderRadius: 2.5,
        p: 2,
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
      }}>
        <TextField
          placeholder="Buscar agentes..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{
            flex: 1,
            maxWidth: 400,
            '& .MuiOutlinedInput-root': {
              background: 'rgba(255,255,255,0.8)',
              borderRadius: 2,
              '&:hover': { background: 'rgba(255,255,255,0.95)' }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#9ca3af' }} />
              </InputAdornment>
            )
          }}
        />

        <Box sx={{ display: 'flex', gap: 1 }}>
          {['all', 'atendimento', 'suporte', 'vendas'].map((type) => (
            <Chip
              key={type}
              label={type === 'all' ? 'Todos' : type.charAt(0).toUpperCase() + type.slice(1)}
              onClick={() => setFilterType(type)}
              sx={{
                px: 1,
                fontWeight: 600,
                background: filterType === type
                  ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                  : 'rgba(255,255,255,0.8)',
                color: filterType === type ? '#fff' : '#374151',
                border: filterType === type ? 'none' : '1px solid rgba(0,0,0,0.1)',
                '&:hover': {
                  background: filterType === type
                    ? 'linear-gradient(135deg, #4f46e5, #7c3aed)'
                    : 'rgba(255,255,255,0.95)'
                }
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Agents Grid */}
      {filteredAgents.length === 0 ? (
        <Box sx={{
          ...glassStyle,
          borderRadius: 3,
          p: 6,
          textAlign: 'center'
        }}>
          <SmartToyIcon sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
            Nenhum agente encontrado
          </Typography>
          <Typography variant="body2" sx={{ color: '#9ca3af', mb: 3 }}>
            {search ? 'Tente uma busca diferente' : 'Crie seu primeiro agente de IA'}
          </Typography>
          {!search && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              }}
            >
              Criar Primeiro Agente
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 3 }}>
          {filteredAgents.map((agent) => {
            const typeColor = getTypeColor(agent.type);
            const providerInfo = getProviderIcon(agent.provider);

            return (
              <Box key={agent.id} sx={glassCardStyle}>
                <Box sx={{ p: 3 }}>
                  {/* Card Header */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${typeColor.text}20, ${typeColor.text}10)`,
                        border: `1px solid ${typeColor.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 22
                      }}>
                        {providerInfo.icon}
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f2937' }}>
                          {agent.name}
                        </Typography>
                        <Chip
                          label={agent.type_display || agent.type}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 600,
                            background: typeColor.bg,
                            color: typeColor.text,
                            border: `1px solid ${typeColor.border}`
                          }}
                        />
                      </Box>
                    </Box>

                    <Chip
                      label={agent.is_active ? 'Ativo' : 'Inativo'}
                      size="small"
                      onClick={() => handleToggleAgent(agent)}
                      sx={{
                        height: 24,
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: agent.is_active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                        color: agent.is_active ? '#10b981' : '#6b7280',
                        border: `1px solid ${agent.is_active ? 'rgba(16, 185, 129, 0.3)' : 'rgba(107, 114, 128, 0.3)'}`,
                        '&:hover': {
                          background: agent.is_active ? 'rgba(16, 185, 129, 0.2)' : 'rgba(107, 114, 128, 0.2)',
                        }
                      }}
                    />
                  </Box>

                  {/* Description */}
                  <Typography variant="body2" sx={{
                    color: '#6b7280',
                    mb: 2,
                    minHeight: 40,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {agent.description || 'Sem descrição'}
                  </Typography>

                  {/* Info */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1.5,
                    p: 2,
                    borderRadius: 2,
                    background: 'rgba(0,0,0,0.02)',
                    mb: 2
                  }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#9ca3af' }}>Provider</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                        {agent.provider.toUpperCase()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#9ca3af' }}>Modelo</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', fontSize: 11 }}>
                        {agent.model}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#9ca3af' }}>Temperatura</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                        {agent.temperature}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#9ca3af' }}>Tools</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                        {agent.enabled_tools?.length || 0} ativas
                      </Typography>
                    </Box>
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      onClick={() => handleEdit(agent)}
                      startIcon={<EditIcon />}
                      fullWidth
                      sx={{
                        py: 1,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        color: '#fff',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                        }
                      }}
                    >
                      Editar
                    </Button>
                    <Tooltip title="Testar Agente">
                      <IconButton
                        onClick={() => handleOpenTest(agent)}
                        sx={{
                          background: 'rgba(16, 185, 129, 0.1)',
                          border: '1px solid rgba(16, 185, 129, 0.2)',
                          '&:hover': { background: 'rgba(16, 185, 129, 0.2)' }
                        }}
                      >
                        <ScienceIcon sx={{ color: '#10b981' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Configurar API Key">
                      <IconButton
                        onClick={() => handleOpenApiKeyModal(agent)}
                        sx={{
                          background: 'rgba(99,102,241,0.1)',
                          border: '1px solid rgba(99,102,241,0.2)',
                          '&:hover': { background: 'rgba(99,102,241,0.2)' }
                        }}
                      >
                        <KeyIcon sx={{ color: '#6366f1' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        onClick={() => handleDelete(agent)}
                        sx={{
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: '1px solid rgba(239, 68, 68, 0.2)',
                          '&:hover': { background: 'rgba(239, 68, 68, 0.2)' }
                        }}
                      >
                        <DeleteIcon sx={{ color: '#ef4444' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Edit/Create Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          color: '#fff',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SmartToyIcon />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {editingAgent ? 'Editar Agente' : 'Novo Agente de IA'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {editingAgent ? 'Modifique as configurações' : 'Configure seu novo assistente'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setEditModalOpen(false)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Tabs
          value={configTab}
          onChange={(_, v) => setConfigTab(v)}
          sx={{
            borderBottom: '1px solid #e2e8f0',
            background: '#fff',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 }
          }}
        >
          <Tab icon={<PsychologyIcon />} label="Personalidade" iconPosition="start" />
          <Tab icon={<TuneIcon />} label="Parâmetros" iconPosition="start" />
          <Tab icon={<BuildIcon />} label="Tools" iconPosition="start" />
        </Tabs>

        <DialogContent sx={{ p: 3 }}>
          {configTab === 0 && (
            <Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3 }}>
                <TextField
                  label="Nome do Agente *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { background: '#fff' } }}
                />
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    label="Tipo"
                    sx={{ background: '#fff' }}
                  >
                    <MenuItem value="vendas">Vendas / SDR</MenuItem>
                    <MenuItem value="atendimento">Atendimento</MenuItem>
                    <MenuItem value="suporte">Suporte Técnico</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <TextField
                label="Descrição"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 3, '& .MuiOutlinedInput-root': { background: '#fff' } }}
              />

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
                Prompt do Sistema *
              </Typography>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 2 }}>
                Este é o prompt principal que define o comportamento do agente. Inclua instruções claras sobre personalidade, tom e responsabilidades.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={12}
                value={formData.system_prompt}
                onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: '#fff',
                    fontFamily: 'monospace',
                    fontSize: 13
                  }
                }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10b981' }
                    }}
                  />
                }
                label="Agente Ativo"
                sx={{ mt: 2 }}
              />
            </Box>
          )}

          {configTab === 1 && (
            <Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Provider</InputLabel>
                  <Select
                    value={formData.provider}
                    onChange={(e) => handleProviderChange(e.target.value)}
                    label="Provider"
                    sx={{ background: '#fff' }}
                  >
                    <MenuItem value="openai">OpenAI</MenuItem>
                    <MenuItem value="anthropic">Anthropic (Claude)</MenuItem>
                    <MenuItem value="gemini">Google Gemini</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Modelo</InputLabel>
                  <Select
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    label="Modelo"
                    sx={{ background: '#fff' }}
                  >
                    {(availableModels[formData.provider] || []).map((model) => (
                      <MenuItem key={model.value} value={model.value}>
                        {model.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                    Temperatura: {formData.temperature}
                  </Typography>
                  <Slider
                    value={formData.temperature}
                    onChange={(_, v) => setFormData({ ...formData, temperature: v as number })}
                    min={0}
                    max={1}
                    step={0.1}
                    valueLabelDisplay="auto"
                    sx={{ color: '#6366f1' }}
                  />
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                    0 = Mais preciso | 1 = Mais criativo
                  </Typography>
                </Box>

                <TextField
                  label="Max Tokens"
                  type="number"
                  value={formData.max_tokens}
                  onChange={(e) => setFormData({ ...formData, max_tokens: parseInt(e.target.value) || 2000 })}
                  fullWidth
                  inputProps={{ min: 100, max: 4000 }}
                  helperText="Tamanho máximo da resposta (100-4000)"
                  sx={{ '& .MuiOutlinedInput-root': { background: '#fff' } }}
                />
              </Box>

              {/* Resumo Automático */}
              <Alert severity="info" sx={{ mt: 3 }}>
                Configure quando a IA deve gerar resumos automáticos das conversas para otimizar memória e custos.
              </Alert>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                  Resumir após: {formData.summary_after_messages} mensagens
                </Typography>
                <Slider
                  value={formData.summary_after_messages}
                  onChange={(_, v) => setFormData({ ...formData, summary_after_messages: v as number })}
                  min={0}
                  max={100}
                  step={5}
                  marks={[
                    { value: 0, label: '0 (Nunca)' },
                    { value: 20, label: '20' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' },
                  ]}
                  valueLabelDisplay="auto"
                  sx={{ color: '#8b5cf6' }}
                />
                <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block' }}>
                  Após este número de mensagens, a IA gera um resumo compacto para manter o contexto. 
                  Defina como 0 para desativar.
                </Typography>
              </Box>
            </Box>
          )}

          {configTab === 2 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
                Tools Habilitadas ({formData.enabled_tools?.length || 0})
              </Typography>

              <Alert severity="info" sx={{ mb: 3 }}>
                Habilite as ferramentas que o agente poderá usar durante as conversas.
              </Alert>

              <Box sx={{
                p: 2,
                borderRadius: 2,
                background: '#fff',
                border: '1px solid #e2e8f0'
              }}>
                {availableTools.map((tool) => (
                  <Box key={tool.id} sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                    borderBottom: '1px solid #f1f5f9',
                    '&:last-child': { borderBottom: 'none' }
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                        {tool.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                        {tool.description}
                      </Typography>
                    </Box>
                    <Switch
                      checked={formData.enabled_tools?.includes(tool.id) || false}
                      onChange={() => handleToolToggle(tool.id)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#6366f1' }
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0', background: '#fff' }}>
          <Button onClick={() => setEditModalOpen(false)} sx={{ textTransform: 'none', color: '#6b7280' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' }
            }}
          >
            {saving ? 'Salvando...' : (editingAgent ? 'Salvar Alterações' : 'Criar Agente')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* API Key Modal */}
      <Dialog
        open={apiKeyModalOpen}
        onClose={() => setApiKeyModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <KeyIcon sx={{ color: '#6366f1' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Configurar API Key</Typography>
            {selectedAgentForKey && (
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                {selectedAgentForKey.name}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedAgentForKey && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                A API Key será criptografada e armazenada com segurança.
              </Alert>
              <TextField
                fullWidth
                label="API Key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk-..."
                helperText={`Provider: ${selectedAgentForKey.provider.toUpperCase()}`}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setApiKeyModalOpen(false)} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveApiKey}
            disabled={!apiKey.trim() || savingApiKey}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            }}
          >
            {savingApiKey ? 'Salvando...' : 'Salvar API Key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Modal */}
      <Dialog
        open={testModalOpen}
        onClose={() => setTestModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #10b981, #059669)',
          color: '#fff'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ScienceIcon />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Testar Agente</Typography>
              {testingAgent && (
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {testingAgent.name}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={() => setTestModalOpen(false)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField
            fullWidth
            label="Mensagem de Teste"
            value={testMessage}
            onChange={(e) => setTestMessage(e.target.value)}
            placeholder="Digite uma mensagem para testar o agente..."
            multiline
            rows={3}
            sx={{ mb: 2 }}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleTest()}
          />

          {testResponse && (
            <Box sx={{
              p: 2,
              borderRadius: 2,
              background: '#1f2937',
              color: '#10b981',
              fontFamily: 'monospace',
              fontSize: 13,
              whiteSpace: 'pre-wrap',
              maxHeight: 300,
              overflow: 'auto'
            }}>
              {testResponse}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTestModalOpen(false)} sx={{ textTransform: 'none' }}>
            Fechar
          </Button>
          <Button
            variant="contained"
            onClick={handleTest}
            disabled={testing || !testMessage.trim()}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #10b981, #059669)',
              '&:hover': { background: 'linear-gradient(135deg, #059669, #047857)' }
            }}
          >
            {testing ? 'Testando...' : 'Enviar Teste'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIAgents;
